import { FC, useEffect, useRef, useState } from "react";
import {
  AreWhitelistUsersPresent,
  InsertUpdateUser,
  getAllUserGroups,
  getUserByUserKey,
} from "./ConnectionService";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import ChevronRightIcon from "src/icons/ChevronRight";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  useTheme,
} from "@material-ui/core";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import * as Yup from "yup";
import FormikForm, { FormikStep } from "src/components/ui-components/FormikForm";
import InputField from "src/components/ui-components/InputField";
import SelectField from "src/components/ui-components/SelectField";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { User } from "./Types";
import SwitchField from "src/components/ui-components/SwitchField";
import PhoneInput from "src/components/shared/PhoneInput";
import { Field } from "formik";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { Visibility, VisibilityOff } from "@material-ui/icons";

const defaultForm: User = {
  userKey: null,
  userGroupKey: "",
  username: "",
  password: "",
  fullName: "",
  email: "",
  mobile: "",
  isDisabled: false,
  clientUserKey: "",
  addToWhitelist: false
};

const MobileUsersCreateEdit: FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState(false);
  const [UserGroupsList, setUserGroupsList] = useState([]);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isSwitchDisabled, setIsSwitchDisabled] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [existingPassword, setExistingPassword] = useState(null);
  const [hasPasswordBeenEdited, setHasPasswordBeenEdited] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState(defaultForm);
  const [showPassword, setShowPassword] = useState({ password: false });

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: location?.state?.UserId && true },
      });
    }
  };

  const fetchUserGroups = async () => {
    getAllUserGroups().then((res) => {
      setUserGroupsList(res);
    });
  };

  const AreWhitelistUsersPresentData = async () => {
    setIsLoading(true);
    try {
      const res = await AreWhitelistUsersPresent();
      if (res?.statusCode === 200) {
        if (res.isSuccess) {
          setIsSwitchDisabled(false);
          if (!location?.state?.UserId) {
            setDefaultFormValues((prev) => ({ ...prev, addToWhitelist: true }));
          }
        } else {
          setIsSwitchDisabled(true);
          if (!location?.state?.UserId) {
            setDefaultFormValues((prev) => ({ ...prev, addToWhitelist: false }));
          }
        }
      } else {
        AlertMessage({ type: "error", msg: "Unexpected response from the server." });
      }
    } catch (err) {
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: "error", msg: errMsg.errorMessage });
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsFetching(true);
    fetchUserGroups();
    AreWhitelistUsersPresentData();
    if (location?.state?.UserId) {
      getUserByUserKey(location?.state?.UserId)
        .then((user: User) => {
          resetFormData({
            userKey: user.userKey,
            userGroupKey: user.userGroupKey,
            username: user.username,
            password: user.password,
            fullName: user.fullName,
            email: user.email,
            mobile: user.mobile,
            isDisabled: user.isDisabled,
            clientUserKey: user.clientUserKey,
            addToWhitelist: user.addToWhitelist
          });
          setExistingPassword(user.password);
        })
        .catch(() => {
          AlertMessage({ type: "error", msg: "Failed to get user details" });
        });
    }
    setIsFetching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box>
        <Container maxWidth={false} >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              sx={{ my: 2 }}
              container
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textSecondary" variant="h5">
                    {`${location?.state?.UserId ? `Edit` : `Create`} User`}
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  <Grid item>
                    <Button
                      component={RouterLink}
                      to="/users"
                      sx={{
                        background:
                          "linear-gradient(to bottom, #939ca3, #939ca3)",
                        minWidth: 100,
                      }}
                      variant="contained"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      sx={{
                        background:
                          "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                      component={RouterLink}
                      to=""
                      onClick={formRef?.current?.submitForm}
                      disabled={isLoading}
                      variant="contained"
                    >
                      Save
                      {isLoading && (
                        <CircularProgress
                          color="inherit"
                          thickness={5}
                          size={20}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CardContent>
              {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
              <Box sx={{ display: isFetching ? "none" : "" }}>
                <FormikForm
                  innerRef={formRef}
                  initialValues={defaultFormValues}
                  enableReinitialize
                  onSubmit={async (values): Promise<void> => {
                    setIsLoading(true);
                    var res = await InsertUpdateUser(values);
                    if (Boolean(res)) {
                      navigate("/users");
                    }
                    setIsLoading(false);
                  }}
                >
                  <FormikStep
                    validationSchema={Yup.object({
                      username: Yup.string()
                        .required("Username is required")
                        .nullable()
                        .max(100, "Username cannot exceed 100 characters")
                        .matches(/^\S*$/, "Username cannot contain spaces"),
                      fullName: Yup.string()
                        .nullable()
                        .max(100, "Full Name cannot exceed 100 characters")
                        .matches(/^[A-Za-z\s]+$/, "Please enter valid Full Name")
                        .test(
                          'leading-spaces',
                          'Please enter a valid name',
                          (value, context) => {
                            if (value && (value.startsWith(' ') || value.endsWith(' '))) {
                              return false;
                            } else {
                              return true;
                            }
                          }
                        )
                        .required("Full Name is Required"),
                      email: Yup.string()
                        .nullable()
                        .required("Email is required")
                        .email("Please enter valid email"),
                      mobile: Yup.string()
                        .nullable()
                        .test(
                          'phoneValidation',
                          'Must be a valid Mobile Number',
                          (value, context) => {
                            if (!value) return true;
                            const phoneNumber = parsePhoneNumberFromString(value);
                            if (phoneNumber && phoneNumber.isValid()) {
                              return true;
                            } else {
                              return false;
                            }
                          }
                        ),
                      userGroupKey: Yup.string()
                        .nullable()
                        .required("User Group is required"),
                      password: Yup.string()
                        .nullable()
                        .test("is-not-empty", "Password must meet criteria if provided", function (value) {
                          if (!value || value === existingPassword) return true;

                          if (value.length < 8) return this.createError({ message: "Password must be at least 8 characters long" });
                          if (value.length > 20) return this.createError({ message: "Password must not exceed 20 characters" });
                          if (!/[A-Z]/.test(value)) return this.createError({ message: "Password must contain at least one uppercase letter (A-Z)" });
                          if (!/[a-z]/.test(value)) return this.createError({ message: "Password must contain at least one lowercase letter (a-z)" });
                          if (!/[0-9]/.test(value)) return this.createError({ message: "Password must contain at least one number (0-9)" });
                          if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return this.createError({ message: "Password must contain at least one special character (e.g., !, @, #, $, etc.)" });
                          if (/\s/.test(value)) return this.createError({ message: "Password must not contain any spaces" });
                    
                          const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(value);
                          const hasRepeated = /(.)\1\1/.test(value);
                    
                          if (hasSequence || hasRepeated) {
                            return this.createError({ message: "Password must not contain sequences (e.g., 1234, abcd) or repeated characters (e.g., aaa)" });
                          }
                          return true;
                        }),
                    })}
                  >
                    <Grid spacing={2} container>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Username *" name="username" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Full Name *" name="fullName" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <InputField
                          label="Password"
                          name="password"
                          type={showPassword.password ? "text" : "password"}
                          onFocus={() => {
                            if (!hasPasswordBeenEdited) {
                              formRef.current.setFieldValue("password", "");
                              setHasPasswordBeenEdited(true);
                            }
                          }}
                          onBlur={() => {
                            if (!formRef.current.values.password) {
                              formRef.current.setFieldValue("password", existingPassword);
                              setHasPasswordBeenEdited(false);
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowPassword((prev) => ({ ...prev, password: !prev.password }))
                                  }
                                >
                                  {showPassword.password ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField
                          label="Email *"
                          name="email"
                          disabled={!!location?.state?.UserId}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <Field name="mobile">
                          {({
                            field,
                            form: { errors, handleBlur, values, setErrors, touched },
                            meta,
                          }) => (
                            <PhoneInput
                              label="Mobile Number"
                              id="phoneInput"
                              name="mobile"
                              {...field}
                              onBlur={(e) => {
                                setPhoneTouched(true);
                                handleBlur(e);
                              }}
                              value={values.mobile}
                              error={touched.mobile && Boolean(meta.error)}
                              helperText={touched.mobile && meta.error}
                              onChange={(mob: string): any => {
                                values.mobile = mob || "";
                                if (isPossiblePhoneNumber(values.mobile)) {
                                  const phoneNumber = parsePhoneNumber(
                                    values.mobile
                                  );
                                  if (phoneNumber) {
                                    setErrors({ ...errors, mobile: undefined });
                                  }
                                } else {
                                  if (phoneTouched) {
                                    meta.touched = true;
                                  }
                                  setErrors({
                                    ...errors,
                                    mobile: "Must be a valid phone number",
                                  });
                                }
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <SelectField
                          data={UserGroupsList}
                          dataTextField="userGroup"
                          dataValueField="userGroupKey"
                          name="userGroupKey"
                          label="User Group *"
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Client User Key" name="clientUserKey" />
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{ mt: 1 }}>
                        <SwitchField
                          label="Add Whitelist User"
                          name="addToWhitelist"
                          labelPlacement="start"
                          disabled={isSwitchDisabled}
                        />
                      </Grid>
                    </Grid>
                  </FormikStep>
                </FormikForm>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default MobileUsersCreateEdit;
